import { FC, useState, useEffect, useRef } from 'react';

import { Box, Modal } from '@mui/material';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { useContextSelector as useCS } from 'use-context-selector';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { TUpdate } from 'domain/types';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { showSystemToast } from 'view/components/SystemToast';
import { useUpdateOnboardingStep } from 'view/modals/OnboardingWizard/hooks/useUpdateOnboardingStep';
import DemographicsStep from 'view/modals/OnboardingWizard/shared/DemographicsStep';
import SuccessStep from 'view/modals/OnboardingWizard/shared/SuccessStep';
import WelcomeStep from 'view/modals/OnboardingWizard/shared/WelcomeStep';
import JobSeekerIntroStep from 'view/modals/OnboardingWizard/jobSeeker/IntroStep';
import JobSeekerPersonalInfoStep from 'view/modals/OnboardingWizard/jobSeeker/PersonalInfoStep';
import JobPreferencesStep from 'view/modals/OnboardingWizard/jobSeeker/JobPreferencesStep';
import WorkExperienceStep from 'view/modals/OnboardingWizard/jobSeeker/WorkExperienceStep';
import RecruiterIntroStep from 'view/modals/OnboardingWizard/recruiter/IntroStep';
import RecruiterPersonalInfoStep from 'view/modals/OnboardingWizard/recruiter/PersonalInfoStep';
import HiringNeedsStep from 'view/modals/OnboardingWizard/recruiter/HiringNeedsStep';
import * as S from 'view/modals/OnboardingWizard/styled';
import { useAuthMe } from 'view/pages/auth/hooks/useAuthMe';
import { useUpdateJobSeekerPreferences } from 'view/pages/profile/hooks/useUpdateJobSeekerPreferences';
import { useUpdateProfileSettings } from 'view/pages/profile/hooks/useUpdateProfileSettings';
import { useUpdateRecruiterPreferences } from 'view/pages/profile/hooks/useUpdateRecruiterPreferences';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import DotStepper from './components/DotStepper';

const genderOptions = [
  { id: 1, name: 'Male' },
  { id: 2, name: 'Female' },
  { id: 3, name: 'Non-Binary' },
  { id: 4, name: 'Prefer not to say' },
  { id: 5, name: 'Other' },
];

const ethnicityOptions = [
  { id: 1, name: 'Asian' },
  { id: 2, name: 'Black, including African American' },
  { id: 3, name: 'Hispanic, Latino/a or Latinx' },
  { id: 4, name: 'Middle Eastern or North African' },
  { id: 5, name: 'Native American or Alaska Native' },
  { id: 6, name: 'Pacific Islander, including Native Hawaiian' },
  { id: 7, name: 'White or Caucasian' },
  { id: 8, name: 'Prefer not to say' },
];

const disabilityStatusOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
  { id: 3, name: 'Prefer not to say' },
];

const veteranStatusOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
  { id: 3, name: 'Prefer not to say' },
];

interface WizardContentProps {
  currentStep: number;
  setCurrentStep: (step: number | ((prev: number) => number)) => void;
  isSubmitting: boolean;
  isSaving: boolean;
  onComplete: (formData: any) => Promise<void>;
  onClose: () => void;
  role?: string;
  saveOnboardingStep: (step: number) => void;
  savePersonalInfo: (formData: any) => Promise<boolean>;
  saveDemographics: (formData: any) => Promise<boolean>;
  saveJobPreferences: (formData: any) => Promise<boolean>;
  saveWorkExperience: (formData: any) => Promise<boolean>;
}

const WizardContent: FC<WizardContentProps> = ({
  currentStep,
  setCurrentStep,
  isSubmitting,
  isSaving,
  onComplete,
  onClose,
  role,
  saveOnboardingStep,
  savePersonalInfo,
  saveDemographics,
  saveJobPreferences,
  saveWorkExperience,
}) => {
  const { handleSubmit, getValues } = useFormContext();
  const isJobSeeker = role === RolesEnum.JOB_SEEKER;

  const handleNext = () => {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    saveOnboardingStep(nextStep);
  };

  const handleBack = () => {
    const prevStep = Math.max(0, currentStep - 1);
    setCurrentStep(prevStep);
    saveOnboardingStep(prevStep);
  };

  // Save personal info when leaving step 2
  const handleNextFromPersonalInfo = async () => {
    const formData = getValues();
    const success = await savePersonalInfo(formData);
    if (!success) {
      return; // Stay on current step if save failed
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    saveOnboardingStep(nextStep);
  };

  // Save demographics when leaving step 3
  const handleNextFromDemographics = async () => {
    const formData = getValues();
    const success = await saveDemographics(formData);
    if (!success) {
      return; // Stay on current step if save failed
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    saveOnboardingStep(nextStep);
  };

  const handleSkipDemographics = () => {
    setCurrentStep(4);
    saveOnboardingStep(4);
  };

  // Save job preferences when leaving step 4 (job seekers only)
  const handleNextFromJobPreferences = async () => {
    const formData = getValues();
    const success = await saveJobPreferences(formData);
    if (!success) {
      return; // Stay on current step if save failed
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    saveOnboardingStep(nextStep);
  };

  // Save work experience when leaving step 5 (job seekers only)
  const handleNextFromWorkExperience = async () => {
    const formData = getValues();
    const success = await saveWorkExperience(formData);
    if (!success) {
      return; // Stay on current step if save failed
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    saveOnboardingStep(nextStep);
  };

  const handleFormSubmit = () => {
    handleSubmit(onComplete)();
  };

  // Job Seeker: 7 steps (Welcome -> Intro -> PersonalInfo -> Demographics -> Preferences -> WorkExperience -> Success)
  // Recruiter: 6 steps (Welcome -> Intro -> PersonalInfo -> Demographics -> HiringNeeds -> Success)
  switch (currentStep) {
    case 0:
      return <WelcomeStep onNext={handleNext} />;
    case 1:
      return isJobSeeker ? (
        <JobSeekerIntroStep onNext={handleNext} onBack={handleBack} />
      ) : (
        <RecruiterIntroStep onNext={handleNext} onBack={handleBack} />
      );
    case 2:
      return isJobSeeker ? (
        <JobSeekerPersonalInfoStep
          onNext={handleNextFromPersonalInfo}
          onBack={handleBack}
          isSaving={isSaving}
        />
      ) : (
        <RecruiterPersonalInfoStep
          onNext={handleNextFromPersonalInfo}
          onBack={handleBack}
          isSaving={isSaving}
        />
      );
    case 3:
      return (
        <DemographicsStep
          onNext={handleNextFromDemographics}
          onBack={handleBack}
          onSkip={handleSkipDemographics}
          isSaving={isSaving}
        />
      );
    case 4:
      return isJobSeeker ? (
        <JobPreferencesStep
          onBack={handleBack}
          onNext={handleNextFromJobPreferences}
          isSaving={isSaving}
        />
      ) : (
        <HiringNeedsStep
          onBack={handleBack}
          onComplete={handleFormSubmit}
          isSubmitting={isSubmitting}
        />
      );
    case 5:
      return isJobSeeker ? (
        <WorkExperienceStep
          onNext={handleNextFromWorkExperience}
          onBack={handleBack}
          isSaving={isSaving}
        />
      ) : (
        <SuccessStep onClose={onClose} />
      );
    case 6:
      return <SuccessStep onClose={onClose} />;
    default:
      return <WelcomeStep onNext={handleNext} />;
  }
};

const OnboardingWizard: FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isOpen = useCS(GlobalConfigContext, globalConfigSelectors.isOpenOnboardingWizard);
  const update: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );
  const userData = useCS(GlobalConfigContext, globalConfigSelectors.userData);
  const role = userData?.role?.name;
  const isJobSeeker = role === RolesEnum.JOB_SEEKER;
  const jobSeekerProfile = userData?.jobSeekerProfile;
  const recruiterProfile = userData?.recruiterProfile;

  const { updateProfileSettings, error: profileSettingsError } = useUpdateProfileSettings();
  const { updateJobSeekerPreferences } = useUpdateJobSeekerPreferences();
  const { updateRecruiterPreferences } = useUpdateRecruiterPreferences();
  const { send: createMembership } = useRequest({
    url: '/api/v1/memberships',
    method: ApiMethodsEnum.Post,
    sendOnStart: false,
    body: {},
  });
  const { send: authMe } = useAuthMe(false);
  const { saveOnboardingStep } = useUpdateOnboardingStep();

  // Track if we're waiting for a save to show specific error messages
  const pendingSaveRef = useRef(false);

  // Track if we've already initialized the step (to avoid resetting on userData changes)
  const hasInitializedStepRef = useRef(false);

  // Get initial step from backend - only on first open
  useEffect(() => {
    if (isOpen && !hasInitializedStepRef.current) {
      const profile = role === RolesEnum.JOB_SEEKER ? jobSeekerProfile : recruiterProfile;
      const savedStep = profile?.onboardingStep || 0;
      setCurrentStep(savedStep);
      hasInitializedStepRef.current = true;
    }
    // Reset the flag when wizard closes so it re-initializes next time
    if (!isOpen) {
      hasInitializedStepRef.current = false;
    }
  }, [isOpen, role, jobSeekerProfile, recruiterProfile]);

  // Show specific error messages when profile settings save fails
  useEffect(() => {
    if (profileSettingsError && pendingSaveRef.current) {
      pendingSaveRef.current = false;
      // Extract error messages from the error object
      const errorMessages = Object.values(profileSettingsError).filter(Boolean);
      const message =
        errorMessages.length > 0
          ? errorMessages.join('. ')
          : 'Failed to save. Please check your input and try again.';
      showSystemToast('error', 'Validation Error', message);
    }
  }, [profileSettingsError]);

  // Default form values
  const defaultValues = {
    // Personal Info
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    contactEmail: userData?.email || '',
    contactPhone: userData?.contactPhone || '',
    currentLocation: userData?.currentLocation || null,
    showEmail: userData?.privacySettings?.showEmail || false,
    showPhone: userData?.privacySettings?.showPhone || false,

    // Demographics
    gender: userData?.demographicInfo?.gender
      ? genderOptions.find((option) => {
          return option.name === userData.demographicInfo.gender;
        }) || null
      : null,
    ethnicity: userData?.demographicInfo?.ethnicity
      ? ethnicityOptions.find((option) => {
          return option.name === userData.demographicInfo.ethnicity;
        }) || null
      : null,
    disabilityStatus: userData?.demographicInfo?.disabilityStatus
      ? disabilityStatusOptions.find((option) => {
          return option.name === userData.demographicInfo.disabilityStatus;
        }) || null
      : null,
    veteranStatus: userData?.demographicInfo?.veteranStatus
      ? veteranStatusOptions.find((option) => {
          return option.name === userData.demographicInfo.veteranStatus;
        }) || null
      : null,

    // Job Seeker / Recruiter preferences (industry and jobTitle used by both)
    industry: jobSeekerProfile?.industry || recruiterProfile?.industry || null,
    jobCategory: jobSeekerProfile?.jobCategory || null,
    jobTitle: jobSeekerProfile?.jobTitle || recruiterProfile?.jobTitle || null,
    salaryRange: {
      from: jobSeekerProfile?.salaryMin?.toString() || '',
      to: jobSeekerProfile?.salaryMax?.toString() || '',
    },
    jobLocation: jobSeekerProfile?.location || null,
    remote: jobSeekerProfile?.jobLocation?.remote || false,
    hybrid: jobSeekerProfile?.jobLocation?.hybrid || false,
    onSite: jobSeekerProfile?.jobLocation?.onSite || false,
    qualifications: jobSeekerProfile?.qualifications || null,
    skills: jobSeekerProfile?.skills || null,

    // Recruiter preferences
    recruiterType: recruiterProfile?.recruiterType || 'representative',
    company: recruiterProfile?.company || null,

    // Work Experience (job seekers only)
    workExperience: jobSeekerProfile?.workExperience || [],
  };

  // Save personal info when leaving step 2 (silent mode for onboarding)
  // Returns true if save was successful, false if there was an error
  const savePersonalInfo = async (formData: any): Promise<boolean> => {
    setIsSaving(true);
    pendingSaveRef.current = true;
    try {
      const avatarId = userData?.photo?.id || null;
      const result = await updateProfileSettings(
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          currentLocation: formData.currentLocation,
          showEmail: formData.showEmail,
          showPhone: formData.showPhone,
        },
        avatarId,
        true // silent mode
      );
      // useRequest returns undefined on error, data on success
      if (!result) {
        // Error toast will be shown by the useEffect watching profileSettingsError
        return false;
      }
      pendingSaveRef.current = false;
      return true;
    } catch {
      pendingSaveRef.current = false;
      showSystemToast('error', 'Error', 'Failed to save. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Save demographics when leaving step 3 (silent mode for onboarding)
  // Returns true if save was successful, false if there was an error
  const saveDemographics = async (formData: any): Promise<boolean> => {
    setIsSaving(true);
    pendingSaveRef.current = true;
    try {
      const avatarId = userData?.photo?.id || null;
      const result = await updateProfileSettings(
        {
          gender: formData.gender,
          ethnicity: formData.ethnicity,
          disabilityStatus: formData.disabilityStatus,
          veteranStatus: formData.veteranStatus,
        },
        avatarId,
        true // silent mode
      );
      // useRequest returns undefined on error, data on success
      if (!result) {
        // Error toast will be shown by the useEffect watching profileSettingsError
        return false;
      }
      pendingSaveRef.current = false;
      return true;
    } catch {
      pendingSaveRef.current = false;
      showSystemToast('error', 'Error', 'Failed to save. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Save job preferences when leaving step 4 (silent mode for onboarding, job seekers only)
  // Returns true if save was successful, false if there was an error
  const saveJobPreferences = async (formData: any): Promise<boolean> => {
    setIsSaving(true);
    try {
      const result = await updateJobSeekerPreferences(
        {
          industry: formData.industry,
          jobCategory: formData.jobCategory,
          jobTitle: formData.jobTitle,
          salaryRange: formData.salaryRange,
          onSite: formData.onSite,
          remote: formData.remote,
          hybrid: formData.hybrid,
          jobLocation: formData.jobLocation,
          qualifications: formData.qualifications,
          skills: formData.skills,
        },
        true // silent mode
      );
      if (!result) {
        showSystemToast('error', 'Error', 'Failed to save job preferences. Please try again.');
        return false;
      }
      return true;
    } catch {
      showSystemToast('error', 'Error', 'Failed to save job preferences. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Save work experience when leaving step 5 (silent mode for onboarding)
  // Returns true if save was successful, false if there was an error
  const saveWorkExperience = async (formData: any): Promise<boolean> => {
    setIsSaving(true);
    try {
      const result = await updateJobSeekerPreferences(formData, true);
      if (!result) {
        showSystemToast('error', 'Error', 'Failed to save work experience. Please try again.');
        return false;
      }
      return true;
    } catch {
      showSystemToast('error', 'Error', 'Failed to save work experience. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Close wizard handler
  const handleClose = () => {
    if (update) {
      update({ isOpenOnboardingWizard: false });
    }
  };

  const handleComplete = async (formData: any) => {
    setIsSubmitting(true);
    try {
      // Save personal info and demographics (silent mode)
      const avatarId = userData?.photo?.id || null;
      await updateProfileSettings(
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          currentLocation: formData.currentLocation,
          showEmail: formData.showEmail,
          showPhone: formData.showPhone,
          gender: formData.gender,
          ethnicity: formData.ethnicity,
          disabilityStatus: formData.disabilityStatus,
          veteranStatus: formData.veteranStatus,
        },
        avatarId,
        true // silent mode
      );

      // Save role-specific preferences (silent mode)
      if (role === RolesEnum.JOB_SEEKER) {
        await updateJobSeekerPreferences(
          {
            industry: formData.industry,
            jobCategory: formData.jobCategory,
            jobTitle: formData.jobTitle,
            salaryRange: formData.salaryRange,
            onSite: formData.onSite,
            remote: formData.remote,
            hybrid: formData.hybrid,
            jobLocation: formData.jobLocation,
            qualifications: formData.qualifications,
            skills: formData.skills,
          },
          true // silent mode
        );
      } else if (role === RolesEnum.RECRUITER) {
        await updateRecruiterPreferences(
          {
            industry: formData.industry,
            jobTitle: formData.jobTitle,
            recruiterType: formData.recruiterType,
          },
          true // silent mode
        );

        // For representative recruiters, bind the company via POST /memberships (§3 of docs).
        // Passing the user's account email as workEmail triggers the verification email automatically.
        if (formData.recruiterType === 'representative' && formData.company) {
          await createMembership({
            company: { id: formData.company.id },
            role: 'INTERNAL_RECRUITER',
            workEmail: userData?.email,
          });
        }
      }

      // Refresh user data (don't fail if this errors)
      try {
        await authMe();
      } catch {
        // Ignore auth refresh errors
      }
    } catch (error) {
      showSystemToast('error', 'Error', 'Failed to save profile. Please try again.');
    } finally {
      // Always show success step and stop submitting
      // Job seekers: step 6, Recruiters: step 5
      setCurrentStep(role === RolesEnum.JOB_SEEKER ? 6 : 5);
      setIsSubmitting(false);
    }
  };

  const handleSkipStep = () => {
    if (currentStep === 3) {
      const nextStep = 4;
      setCurrentStep(nextStep);
      saveOnboardingStep(nextStep);
      return;
    }
    if (currentStep === 5 && isJobSeeker) {
      const nextStep = 6;
      setCurrentStep(nextStep);
      saveOnboardingStep(nextStep);
    }
  };

  const isSkippableStep = currentStep === 3 || (currentStep === 5 && isJobSeeker);
  const isSuccessStep = isJobSeeker ? currentStep === 6 : currentStep === 5;

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        // Prevent closing by clicking outside
      }}
      aria-labelledby="onboarding-wizard"
      disableEscapeKeyDown
    >
      <S.WizardWrapper>
        {!isSuccessStep && (
          <Box
            style={{
              paddingBottom: 32,
              position: 'relative',
            }}
          >
            <DotStepper totalSteps={isJobSeeker ? 6 : 5} currentStep={currentStep} />
            {isSkippableStep && (
              <S.StepperWithSkip>
                <S.SkipButton onClick={handleSkipStep}>Skip</S.SkipButton>
              </S.StepperWithSkip>
            )}
          </Box>
        )}
        <FormContainer
          key={JSON.stringify(defaultValues)}
          defaultValues={defaultValues}
          onSuccess={handleComplete}
        >
          <S.ScrollableWizardBody>
            <WizardContent
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              isSubmitting={isSubmitting}
              isSaving={isSaving}
              onComplete={handleComplete}
              onClose={handleClose}
              role={role}
              saveOnboardingStep={saveOnboardingStep}
              savePersonalInfo={savePersonalInfo}
              saveDemographics={saveDemographics}
              saveJobPreferences={saveJobPreferences}
              saveWorkExperience={saveWorkExperience}
            />
          </S.ScrollableWizardBody>
        </FormContainer>
      </S.WizardWrapper>
    </Modal>
  );
};

export default OnboardingWizard;
